import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { api } from '../../utils/index'
import SetTitle from '../../utils/set_title'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { notify } from '../../utils/notify'
import FileInput from '../../components/molecules/FileInput'
import ClienteSelect from '../../components/molecules/ClienteSelect'
import { useFileUpload } from '../../hooks'

function VolumeRetrieveEditForm() {
  SetTitle('Market Screener')

  const { id } = useParams()
  const history = useHistory()

  // Record data (read-only except Monthly Update)
  const [volumeRetrieve, setVolumeRetrieve] = useState({
    name: '',
    schedule: false,
    locale: '',
    province: '',
    email: '',
    bq_table_name: '',
    geo_index: false,
    granularity: '',
  })

  const [file, setFile] = useState(null)
  const [clienti, setClienti] = useState([])
  const [selectedClientId, setSelectedClientId] = useState('')
  const { upload, loading: saving } = useFileUpload()
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const [keywordStats, setKeywordStats] = useState({ total: 0, filled: 0 })
  const [keywordStatsLoading, setKeywordStatsLoading] = useState(true)

  // Fetch record on report load and check if the report is retrieving data rn
  useEffect(() => {
    async function load() {
      try {
        // fetch the form data
        const resp = await api.get(`/market-screener-detail/${id}`)
        setVolumeRetrieve(resp.data)
        setSelectedClientId(resp.data?.cliente?.id ? String(resp.data.cliente.id) : '')
        setLoading(false)

        // fetch the keywords filled over the total
        const keywordStatsResp = await api.get('keyword-volume/', {
          params: { volume_report_scheduled_id: id },
        })
        setKeywordStats({
          total: keywordStatsResp.data.total,
          filled: keywordStatsResp.data.filled,
        })
      } catch (err) {
        setError('Error fetching record or keyword Statistic.')
      } finally {
        setLoading(false)
        setKeywordStatsLoading(false)
      }
    }
    load()
  }, [id])

  useEffect(() => {
    api
      .get('clienti/')
      .then((response) => {
        setClienti(Array.isArray(response.data) ? response.data : response.data?.results || [])
      })
      .catch((err) => {
        console.error('Failed to fetch clienti', err)
      })
  }, [])

  // can only edit once keywordStats are loaded and the report isn't running
  const canEdit =
    !keywordStatsLoading &&
    (keywordStats.filled === keywordStats.total || keywordStats.filled === 0)

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!selectedClientId) {
      notify('Seleziona un cliente.', 'error')
      return
    }

    const formData = new FormData()

    // Append CSV file if selected
    if (file) {
      formData.append('file', file)
    }

    // Append all other fields (read-only but sent for completeness)
    formData.append('name', volumeRetrieve.name)
    formData.append('schedule', volumeRetrieve.schedule)
    formData.append('locale', volumeRetrieve.locale)
    formData.append('province', volumeRetrieve.province)
    formData.append('geo_index', volumeRetrieve.geo_index)
    if (volumeRetrieve.geo_index) {
      formData.append('granularity', volumeRetrieve.granularity)
    }
    formData.append('email', volumeRetrieve.email)
    formData.append('bq_table_name', volumeRetrieve.bq_table_name)
    formData.append('cliente_id', selectedClientId)

    try {
      await upload('put', `/market-screener-detail/${id}`, formData, {
        onSuccess: () => {
          notify('Keywords Aggiornate Correttamente', 'success')
          setTimeout(() => {
            history.push('/market-screener')
          }, 3000) // 3-seconds delay
        },
      })
    } catch (err) {
      notify('Errore nel salvataggio', 'error')
      console.error('Save failed:', err)
    }
  }
  if (loading) {
    return <Spinner />
  }
  if (error) {
    return <div className="text-center text-danger">{error}</div>
  }

  return (
    <>
      <div className="pb-5">
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row style={{ marginLeft: '0px' }}>
              <Col md={10}>
                <h1 className="mb-0">Market Screener</h1>
              </Col>
              <Col md={2} style={{ textAlign: 'right' }}>
                <Button color="warning" onClick={() => history.push('/market-screener')}>
                  &lt;- Go Back
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {/* Show warning if we’re still fetching keywordStats or not yet complete */}
            {keywordStatsLoading ? (
              <Alert color="info">Checking keyword progress...</Alert>
            ) : (
              !canEdit && (
                <Alert color="warning">
                  This report is still running. You can only edit once all keywords are retrieved (
                  {keywordStats.filled}/{keywordStats.total}).
                </Alert>
              )
            )}
            <Form onSubmit={handleSubmit} id="market-screener-form">
              {/* --- Row 1: File Input --- */}
              <Row>
                <Col md={6}>
                  <FileInput
                    onFileChange={(file) => setFile(file)}
                    accept=".csv, .xlsx"
                    helpText={
                      <>
                        Carica un file contenente le tue keywords. <br />
                        <b>Il file deve contenere almeno una colonna "Keyword"</b> <br />
                        <b>
                          Le singole keyword non devono contenere questi simboli: @ \ ^ = ! ` {'<'}{' '}
                          {'>'} {'['} {']'} {'('} {')'} % | ? ; ~
                        </b>
                      </>
                    }
                    required={false}
                  />
                  {file && (
                    <Alert color="warning" className="mt-3 mb-0">
                      Attenzione: modificando il file verranno cancellati i dati storici del report
                      su BigQuery per mantenere coerenza.
                    </Alert>
                  )}
                </Col>
              </Row>
              <br />

              <Row>
                <Col md={6}>
                  <ClienteSelect
                    id="select-client"
                    label="Cliente canonico"
                    clienti={clienti}
                    selectedClientId={selectedClientId}
                    onChange={setSelectedClientId}
                    isRequired
                    showCreateLink
                  />
                </Col>
              </Row>
              <br />

              {/* --- Row 2: Nome & Email --- */}
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="nome" style={{ display: 'block' }}>
                      Nome
                    </Label>
                    <Input
                      className="form-control-alternative"
                      type="text"
                      name="nome"
                      id="nome"
                      value={volumeRetrieve.name}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="email" style={{ display: 'block' }}>
                      Email
                    </Label>
                    <Input
                      className="form-control-alternative"
                      type="email"
                      name="email"
                      id="email"
                      value={volumeRetrieve.email}
                      readOnly
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* --- Row 3: Location & Geo Index --- */}
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="location" style={{ display: 'block' }}>
                      Location
                    </Label>
                    <Input
                      className="form-control-alternative"
                      type="text"
                      name="location"
                      id="location"
                      value={volumeRetrieve.locale}
                      readOnly
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label htmlFor="geoIndex" style={{ display: 'block' }}>
                      Geo Index
                    </Label>
                    <Input
                      type="checkbox"
                      id="geoIndex"
                      checked={!!volumeRetrieve.geo_index}
                      readOnly
                      disabled
                      style={{ width: 'auto', marginLeft: '0px' }}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* --- Row 4: Province/Granularity & Monthly Update --- */}
              <Row className="mb-3">
                <Col md={6}>
                  {!volumeRetrieve.geo_index ? (
                    <FormGroup>
                      <Label for="province" style={{ display: 'block' }}>
                        Province
                      </Label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        name="province"
                        id="province"
                        value={volumeRetrieve.province}
                        readOnly
                      />
                    </FormGroup>
                  ) : (
                    <FormGroup>
                      <Label for="granularity" style={{ display: 'block' }}>
                        Granularità
                      </Label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        name="granularity"
                        id="granularity"
                        value={volumeRetrieve.granularity || ''}
                        readOnly
                      />
                    </FormGroup>
                  )}
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label htmlFor="schedule" style={{ display: 'block' }}>
                      Monthly Update
                    </Label>
                    <Input
                      type="checkbox"
                      id="schedule"
                      checked={volumeRetrieve.schedule}
                      disabled={volumeRetrieve.geo_index}
                      onChange={(e) =>
                        setVolumeRetrieve({
                          ...volumeRetrieve,
                          schedule: e.target.checked,
                        })
                      }
                      style={{ width: 'auto', marginLeft: '0px' }}
                    />
                    <br />
                    <div style={{ marginTop: '8px' }}>
                      <FormText color="muted" style={{ display: 'block' }}>
                        Se disattivi questa opzione, verranno rimosse le pianificazioni future.
                      </FormText>
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              {/* --- Row 5: Submit Button --- */}
              <Row style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '50px' }}>
                <Button color="primary" type="submit" disabled={saving || !canEdit}>
                  {saving ? 'Saving...' : 'Save File & Update'}
                </Button>
              </Row>
            </Form>
            <ToastContainer />
          </CardBody>
        </Card>
      </div>
    </>
  )
}

export default VolumeRetrieveEditForm
